<template>
  <v-row>
    <v-col cols="12" md="4">
      <v-hover
        v-slot="{ hover }"
        :disabled="party.techniciansOnly && !user.MasterPermission"
      >
        <v-img :src="basic.cover" class="rounded" :aspect-ratio="16 / 9">
          <v-fade-transition leave-absolute>
            <v-card
              v-if="hover || !basic.cover || fileLoading"
              @click="changeCover"
              class="d-flex transition-fast-in-fast-out grey darken-2 v-card--reveal text-h2 white--text justify-center align-center"
              style="height: 100%; opacity: 0.8"
            >
              <v-progress-circular indeterminate v-if="fileLoading" />
              <v-icon class="white--text" v-else>mdi-camera</v-icon>
            </v-card>
          </v-fade-transition>
        </v-img>
      </v-hover>
      <div class="d-flex justify-center my-2" v-if="config.opMode">
        <v-btn
          @click="downloadCover"
          small
          :loading="loadingCoverDownload"
          :disabled="successDownloadCover"
          block
          color="primary"
          depressed
          text
        >
          <template v-if="successDownloadCover">
            <v-icon left small>mdi-check</v-icon> Capa baixada com sucesso
          </template>
          <template v-else>
            <v-icon left small>mdi-tray-arrow-down</v-icon> Baixar capa
          </template>
        </v-btn>
      </div>
      <small class="text--secondary text-center d-block">
        A resolução recomendada é <b>1280x720</b>. A capa sempre será exibida na
        proporção <b>16/9</b>.
      </small>
      <input
        type="file"
        accept="image/*"
        ref="coverInput"
        @change="onFileChange"
        v-show="false"
      />
    </v-col>
    <v-col cols="12" md="8">
      <v-text-field
        v-model="basic.name"
        :counter="50"
        :rules="rules.name"
        label="Nome do evento"
        required
        outlined
        :readonly="party.techniciansOnly && !user.MasterPermission"
      ></v-text-field>
      <v-text-field
        v-model="basic.subtitle"
        :counter="30"
        dense
        :rules="rules.subtitle"
        label="Subtítulo"
        required
        outlined
        :readonly="party.techniciansOnly && !user.MasterPermission"
      ></v-text-field>

      <v-select
        v-model="basic.categoryId"
        :items="categories"
        item-text="name"
        item-value="id"
        label="Categoria"
        outlined
        clearable
        :loading="loadingCategories"
        :readonly="party.techniciansOnly && !user.MasterPermission"
        :disabled="loadingCategories"
        dense
        @change="onCategoryChange"
      >
        <template v-slot:item="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="24" color="primary" class="white--text mr-2">
              <v-img
                v-if="item.icon"
                :src="item.icon"
                :alt="item.name"
                contain
              />
              <span v-else class="text-caption">
                {{ item.name.charAt(0).toUpperCase() }}
              </span>
            </v-avatar>
            <span>{{ item.name }}</span>
          </div>
        </template>
        <template v-slot:selection="{ item }">
          <div class="d-flex align-center">
            <v-avatar
              size="20"
              color="primary"
              class="white--text mr-2"
            >
              <v-img
                v-if="item.icon"
                :src="item.icon"
                :alt="item.name"
                contain
              />
              <span v-else class="text-caption">
                {{ item.name.charAt(0).toUpperCase() }}
              </span>
            </v-avatar>
            <span>{{ item.name }}</span>
          </div>
        </template>
      </v-select>

      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="basic.date"
            label="Data"
            outlined
            type="datetime-local"
            required
            hide-details
            :readonly="party.techniciansOnly && !user.MasterPermission"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="basic.endDate"
            label="Data final"
            outlined
            type="datetime-local"
            required
            hide-details
            :rules="rules.endDate"
            :readonly="party.techniciansOnly && !user.MasterPermission"
          />
        </v-col>
      </v-row>

      <base-editor
        label="Descrição"
        v-model="basic.description"
        :readonly="party.techniciansOnly && !user.MasterPermission"
      />
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters } from "vuex";
import PARTY from "@/services/admin/party";
import { getCategories } from "@/services/api";
import moment from "moment";

export default {
  data() {
    return {
      basic: {
        youtubeId: this.party?.youtubeId,
        cover: this.party?.cover,
        name: this.party?.name,
        description: this.party?.description,
        categoryId: this.party?.categoryId,
        date: this.party?.date ? this.dateToString(this.party.date) : undefined,
        endDate: this.party?.endDate
          ? this.dateToString(this.party.endDate)
          : undefined,
      },
      loadingCoverDownload: false,
      successDownloadCover: false,
      fileLoading: false,
      loadingCategories: false,
      categories: [],
      rules: {
        name: [
          (v) => !!v || "Name é obrigatório",
          (v) =>
            (v && v.length <= 50) || "Name deve ter menos de 50 caracteres",
        ],
        subtitle: [
          (v) =>
            !v || v.length <= 30 || "Subtítulo deve ter menos de 30 caracteres",
        ],
        endDate: [
          (v) => !!v || "Data final é obrigatório",
          (v) => {
            if (!v) return true;
            const date = new Date(v);
            const start = new Date(this.basic.date);
            return date <= start
              ? "Data final deve ser maior que a data inicial"
              : true;
          },
        ],
      },
    };
  },
  methods: {
    dateToString(date) {
      return moment(date).format("YYYY-MM-DDTHH:mm");
    },
    hasChanges() {
      if (this.basic.name !== this.party.name) return true;
      if (this.basic.subtitle !== this.party.subtitle) return true;
      if (this.basic.description !== this.party.description) return true;
      if (this.basic.categoryId !== this.party.categoryId) return true;
      if (!moment(this.basic.date).isSame(this.party.date)) return true;
      if (!moment(this.basic.endDate).isSame(this.party.endDate)) return true;
      return false;
    },
    save() {
      if (!this.hasChanges()) return false;
      return PARTY.update({
        id: this.party.id,
        organizationId: this.party.organizationId,

        name: this.basic.name,
        subtitle: this.basic.subtitle,
        description: this.basic.description,
        categoryId: this.basic.categoryId,
        youtubeId: this.basic.youtubeId ? this.basic.youtubeId : undefined,
        date: this.basic.date ? new Date(this.basic.date) : undefined,
        endDate: this.basic.endDate ? new Date(this.basic.endDate) : undefined,
      });
    },
    changeCover() {
      if (this.fileLoading) return;
      this.$refs.coverInput.click();
    },
    async onFileChange(e) {
      try {
        this.fileError = false;
        this.fileLoading = true;

        const file = e.target.files[0];
        let response = await PARTY.cover(
          this.party.organizationId,
          this.party.id,
          file
        );
        this.partyBkp.cover = response.party.cover;
      } catch (error) {
        this.fileError = error.message;
      } finally {
        this.fileLoading = false;
      }
    },
    formatName(name) {
      return name
        .split(" ")
        .map((n) => (n.length > 3 ? n.charAt(0).toUpperCase() + n.slice(1) : n))
        .join(" ");
    },
    async downloadCover() {
      try {
        this.loadingCoverDownload = true;
        if (!this.basic.cover) {
          throw new Error("Nenhuma imagem de capa disponível para download.");
        }

        const response = await fetch(this.basic.cover, {
          headers: {
            "Access-Control-Allow-Origin": "*",
          },
          // mode: "no-cors",
        });
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `capa_${this.basic.name || "evento"}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.successDownloadCover = true;
        setTimeout(() => {
          this.successDownloadCover = false;
        }, 3000);
      } catch (error) {
        console.error("Erro ao baixar a capa:", error);
      } finally {
        this.loadingCoverDownload = false;
      }
    },
    async loadCategories() {
      try {
        this.loadingCategories = true;
        const response = await getCategories();
        this.categories = response.categories || [];
      } catch (error) {
        console.error("Erro ao carregar categorias:", error);
      } finally {
        this.loadingCategories = false;
      }
    },
  },
  computed: {
    ...mapGetters("auth", ["config", "user"]),
  },
  watch: {
    party: {
      handler: function (val, oldVal) {
        if (!val) return;
        this.basic = {
          name: val.name,
          subtitle: val.subtitle,
          description: val.description,
          cover: val.cover,
          categoryId: val.categoryId,
          date: val.date ? this.dateToString(val.date) : undefined,
          endDate: val.endDate ? this.dateToString(val.endDate) : undefined,
        };

        // Carregar categorias quando o party mudar
        this.loadCategories();
      },
      deep: true,
    },
    "basic.name": {
      handler(val) {
        if (val) this.basic.name = this.formatName(val);
      },
      immediate: true,
    },
  },
  props: {
    party: {
      type: Object,
      required: true,
    },
  },
  mounted() {
    this.loadCategories();
  },
};
</script>

<style></style>
